import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RumAuditService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly subs = new Subscription();
  private currentRoute = '/';

  init(): void {
    if (!environment.datadog?.enabled) return;

    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => (this.currentRoute = e.urlAfterRedirects)),
    );

    this.subs.add(
      fromEvent<MouseEvent>(document, 'click').subscribe((event) => {
        const target = event.target as HTMLElement;
        const actionName = this.resolveActionName(target);
        if (!actionName) return;

        datadogRum.addAction('user_interaction', {
          action: actionName,
          element: target.tagName.toLowerCase(),
          route: this.currentRoute,
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private resolveActionName(target: HTMLElement): string | null {
    const interactive = target.closest('button, a, [data-audit-action], input, select');
    if (!interactive) return null;

    const el = interactive as HTMLElement;

    const custom =
      el.getAttribute('data-audit-action') ??
      el.closest('[data-audit-action]')?.getAttribute('data-audit-action');
    if (custom) return custom;

    const aria = el.getAttribute('aria-label');
    if (aria) return aria.trim();

    const text = el.textContent?.trim().replace(/\s+/g, ' ').slice(0, 60);
    if (text) return text;

    return el.tagName.toLowerCase();
  }
}
